import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ThemeMode } from '@/theme/tokens';

const THEME_KEY = 'ol.theme';

function initialTheme(): ThemeMode {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_KEY) : null;
  if (saved === 'light' || saved === 'dark') return saved;
  // Dark PRIMARY (spec §14) — default even if the OS prefers light.
  return 'dark';
}

export interface UiState {
  theme: ThemeMode;
  /** Data-layer edges visible on the canvas (toggle `D`, spec §4.5). Off by default above 15 nodes. */
  dataLayerVisible: boolean;
  /** Long-shift density control (spec §14). */
  density: 'comfortable' | 'compact';
  /** Currently-inspected node id (opens the read-only Inspector). */
  selectedNodeId: string | null;
}

const initialState: UiState = {
  theme: initialTheme(),
  dataLayerVisible: false,
  density: 'comfortable',
  selectedNodeId: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<ThemeMode>) {
      state.theme = action.payload;
    },
    toggleTheme(state) {
      state.theme = state.theme === 'dark' ? 'light' : 'dark';
    },
    setDataLayerVisible(state, action: PayloadAction<boolean>) {
      state.dataLayerVisible = action.payload;
    },
    toggleDataLayer(state) {
      state.dataLayerVisible = !state.dataLayerVisible;
    },
    setDensity(state, action: PayloadAction<UiState['density']>) {
      state.density = action.payload;
    },
    selectNode(state, action: PayloadAction<string | null>) {
      state.selectedNodeId = action.payload;
    },
  },
});

export const {
  setTheme,
  toggleTheme,
  setDataLayerVisible,
  toggleDataLayer,
  setDensity,
  selectNode,
} = uiSlice.actions;

export const persistTheme = (theme: ThemeMode) => {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* storage unavailable — non-fatal */
  }
};

export default uiSlice.reducer;
